import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import 'bootstrap/dist/css/bootstrap.min.css';

// Styled components
const MainContainer = styled.div`
  margin: 20px 100px 100px;

  @media (max-width: 768px) {
    margin: 0px 50px 50px;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  overflow-x: auto;
  margin-top: 20px;
  padding: 10px 0;

  @media (max-width: 768px) {
    margin-top: 0px;
    gap: 25px;
  }
`;

const Slide = styled.div`
  width: 300px;
  height: 300px;
  position: relative;
  flex-shrink: 0;
  border-radius: 20px;
  overflow: hidden;
  transition: transform 0.5s ease;

  &:hover {
    transform: translateY(-5px);
    cursor: pointer;
  }

  @media (max-width: 768px) {
    width: 200px;
    height: 200px;
  }

  @media (max-width: 480px) {
    width: 70%;
    height: auto;
  }
`;

const SlideImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 20px;
  transition: transform 0.8s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    border-radius: 15px;
  }

  @media (max-width: 480px) {
    border-radius: 10px;
  }
`;

const Overlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  color: white;
  background: rgba(0, 0, 0, 0.4);
  padding: 10px;
  border-radius: 5px;
  text-align: left;

  h3 {
    font-size: 18px;
    font-weight: 350;
    margin: 10px;

    @media (max-width: 768px) {
      font-size: 14px;
    }

    @media (max-width: 480px) {
      font-size: 12px;
      margin: 5px;
    }
  }
`;

// File type constants
const FILE_PLACE_TYPE = {
  Gallery: 0,
  CategoryPhoto: 1,
  CoverPhoto: 2,
  Poster: 3,
};

const DestinationsCategories = ({ categoryId }) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const tokenString = sessionStorage.getItem('token');
  const { i18n } = useTranslation();
  const selectedLanguage = sessionStorage.getItem('selectedLanguage') || 'en';

  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  const [destinations, setDestinations] = useState([]);

useEffect(() => {
  const fetchDestinationCategories = async () => {
    try {
      const response = await fetch(`${BASE_URL}Destination/getByCategory/${categoryId}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      const rawFileNames = data.flatMap(d => d.fileNames || []);

      // ✅ Filter only files that start with "CategoryPhoto_"
      const categoryPhotoFileNames = rawFileNames.filter(name =>
        name.startsWith("CategoryPhoto_")
      );

      let fetchedFiles = [];
      if (categoryPhotoFileNames.length > 0) {
        const fileResponse = await fetch(`${BASE_URL}Destination/getFiles`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokenString}`,
          },
          body: JSON.stringify(categoryPhotoFileNames), // ✅ Send only filtered names
        });

        if (!fileResponse.ok) throw new Error('Failed to fetch file contents');
        fetchedFiles = await fileResponse.json();
      }

      const updatedDestinations = data.map(dest => {
        const categoryFile = fetchedFiles.find(
          f =>
            f.filePlaceType === FILE_PLACE_TYPE.CategoryPhoto &&
            dest.fileNames?.includes(f.fileName)
        );

        const categoryBase64Src = categoryFile
          ? `data:${categoryFile.mimeType};base64,${categoryFile.base64Content}`
          : null;

        return {
          ...dest,
          categoryBase64Src,
        };
      });

      setDestinations(updatedDestinations);
    } catch (error) {
      console.error('Error fetching destinations or files:', error);
    }
  };

  if (categoryId) {
    fetchDestinationCategories();
  }
}, [categoryId]);


  const handleClick = (id) => {
    navigate('/destinations', {
      state: {
        activeComponent: 'DestinationTemplate',
        destinationId: id,
        category: 'destination',
      },
    });
  };

  return (
    <MainContainer>
      <CarouselContainer>
        {destinations.map(destination => (
          <Slide key={destination.id} onClick={() => handleClick(destination.id)}>
            <SlideImage
              src={
                destination.categoryBase64Src || "Loading..."
              }
              alt={destination.name[selectedLanguage] || destination.name['en']}
            />
            <Overlay>
              <h3>{destination.name[selectedLanguage]}</h3>
            </Overlay>
          </Slide>
        ))}
      </CarouselContainer>
    </MainContainer>
  );
};

export default DestinationsCategories;



